/*
   This file handles building of the MSAL Config object and the MSAL Public client
   use in the hybrid AAD auth flow.    
*/
import * as MSAL from '@citrite/msal-browser';
import { trackAnalyticsEvent } from 'analytics';
import { v4 } from 'uuid';
import { aadSSOEventHandler } from 'Environment/msal/aadEventNotificationHandler';
import { AADSsoEventPayload } from 'Environment/msal/aadSSOEventPayload';
import {
	getUpnOrAuthAliasForCurrentUser,
	isStoreUrlAndRedirectUriOriginMatch,
	wsuiAuthEnvironment,
} from 'Environment/msal/clientInfoUtils';
import { CtxStoreTicket } from 'staticPages/scripts/ctxStoreTicketUtils';

export type AADAuthCodeType = { authcode: string };
export type AuthInfoWithCnf = MSAL.AuthInfoType & { cnf: string; resourceId: string };

/*
   Store only those properties of authInfo which are constant across multiple auth requests.
   Pick only properties we explicitly use for now instead of the entire auth object
   to avoid accidentally using any other properties without review. 
*/
type AuthInfoTypeFromWSUI = Pick<
	AuthInfoWithCnf,
	'redirectUri' | 'cnf' | 'deviceId' | 'resourceId'
>;

/*
  WSUI-11559: Keep a map of correlationId to authInfo object received from WSUI.
  This is needed because MSAL SDK will return the correlationId in the response/error callbacks,
  and we need to map it back to the original authInfo object to send the response/error
  back to WSUI via the browser extension bridge.

  Previously a singlie variable was used which would get overwritten in case of multiple
  concurrent auth requests. This caused mismatches between requests and responses/errors.
*/
const authInfoMapFromWSUI = new Map<string, AuthInfoTypeFromWSUI>();

export function setAuthorizationCode(aadAuthCode: AADAuthCodeType): void {
	MSAL.setAuthorizationCode(aadAuthCode);
}

// allow-unused-export : for unit testing
export function popAuthInfoFromMap(
	correlationId: string
): AuthInfoTypeFromWSUI | undefined {
	if (!correlationId) {
		return undefined;
	}
	const authInfo = authInfoMapFromWSUI.get(correlationId);
	if (authInfo) {
		authInfoMapFromWSUI.delete(correlationId);
	}
	return authInfo;
}

//allow-unused-export for unit testing
export const onAADResponse = (authResult: MSAL.AADAuthResult) => {
	const authInfoFromWSUI = popAuthInfoFromMap(authResult?.correlationId);
	if (!authInfoFromWSUI) {
		return null;
	}
	return aadSSOEventHandler.sendAccessTokenViaBrowserExtensionBridge(
		authInfoFromWSUI.resourceId,
		authInfoFromWSUI.deviceId,
		authResult
	);
};

//allow-unused-export for unit testing
export const onAADError = (error: MSAL.AADErrorResult) => {
	trackAnalyticsEvent(AADSsoEventPayload.tokenError());
	const authInfoFromWSUI = popAuthInfoFromMap(error?.correlationId);
	if (!authInfoFromWSUI) {
		return;
	}
	aadSSOEventHandler.sendTokenErrorViaBrowserExtensionBridge(
		authInfoFromWSUI.resourceId,
		authInfoFromWSUI.deviceId,
		error
	);
};

/**
 * @param redirectUri
 * @returns MSAL.ctxAADInteractionType
 *
 * For the hybrid MSAL auth flow (viz the one via Browser Extension),
 * we have only two options (both https based, no custom-scheme
 * since custom scheme is only for native clients):
 */
// allow-unused-export : for unit testing
export function getInteractionTypeFromRedirectUri(
	redirectUri: string
): MSAL.ctxAADInteractionType {
	if (isStoreUrlAndRedirectUriOriginMatch(redirectUri)) {
		return MSAL.ctxAADInteractionType.REDIRECT_TO_CUSTOMER_MANAGED_HTTPS;
	}

	return MSAL.ctxAADInteractionType.REDIRECT_TO_CTX_HTTPS;
}

export function initMSALSdkForHybridMode(): void {
	const initConfig: MSAL.MsalSdkInitConfig = {
		supportsInteractiveAuthOnly: false,
		interactionType: getInteractionTypeFromRedirectUri,
		onPerformAuthorization: undefined,
		onAADResponse,
		onAADError,
		environment: wsuiAuthEnvironment(),
		getCtxStoreTicket: async () => {
			return await CtxStoreTicket.request();
		},
	};
	MSAL.initMsalSdk(initConfig);
}

export function startHybridAADTokenFlow(authInfo: AuthInfoWithCnf): void {
	authInfo.loginHint = getUpnOrAuthAliasForCurrentUser();
	authInfo.correlationId = authInfo.correlationId ?? v4();
	trackAnalyticsEvent(AADSsoEventPayload.tokenRequested());
	setAuthorizationCode({ authcode: '' });
	authInfoMapFromWSUI.set(authInfo.correlationId, authInfo);
	aadSSOEventHandler.clearEventForResource(authInfo.resourceId);
	MSAL.startAADTokenFlow(authInfo);
}
